/**
 * Altara PWA helpers.
 *
 * Provides:
 *   - registerServiceWorker() — invoked once from src/index.js
 *   - useInstallPrompt() — React hook exposing installability + a
 *     .promptInstall() action for wallet's `<InstallAppPrompt/>`
 *   - useOnlineStatus() — reactive online/offline detector
 *   - offlineQueue — localStorage-backed FIFO for POST payloads. Flushes
 *     on `online` events. Used by the drop-off notification hook (wallet
 *     signal payloads etc.) so signals aren't lost while offline.
 *
 * Deliberately NO IndexedDB dependency: localStorage is fine for the
 * small volumes we queue (single-digit KB per wallet session). If we ever
 * ship real file offline uploads, revisit with idb.
 */
import { useCallback, useEffect, useState } from 'react';

const SW_URL = '/service-worker.js';
const QUEUE_KEY = 'altara:offline-queue:v1';

// ─── Service worker registration ────────────────────────────
export async function registerServiceWorker() {
  if (typeof window === 'undefined') return;
  if (!('serviceWorker' in navigator)) return;
  // Skip in the CRA dev overlay iframe or file:// contexts
  if (window.location.protocol === 'file:') return;

  try {
    const registration = await navigator.serviceWorker.register(SW_URL, { scope: '/' });
    // Force an update check on every load — catches deploys quickly.
    try { await registration.update(); } catch { /* ignore */ }
    // Auto-activate updated workers on next navigation
    registration.addEventListener('updatefound', () => {
      const newSW = registration.installing;
      if (!newSW) return;
      newSW.addEventListener('statechange', () => {
        if (newSW.state === 'installed' && navigator.serviceWorker.controller) {
          // A new SW is waiting. Tell it to take over immediately so the
          // next request goes through the new version. Users on admin
          // routes now get fresh JS without a manual hard-refresh.
          newSW.postMessage({ type: 'SKIP_WAITING' });
        }
      });
    });
    // When the controller changes (i.e. the new SW took over), reload
    // once so the current tab gets the fresh HTML+bundle.
    let hasReloaded = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (hasReloaded) return;
      hasReloaded = true;
      window.location.reload();
    });
    // Trigger queue flush when the worker is up
    if (navigator.onLine) flushQueue().catch(() => {});
  } catch (err) {
    // Never crash the app for SW issues.
    console.warn('[Altara PWA] SW registration failed', err);
  }
}

// ─── Install prompt (beforeinstallprompt) ───────────────────
export function useInstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(
    typeof window !== 'undefined' &&
      (window.matchMedia?.('(display-mode: standalone)').matches ||
        window.navigator?.standalone === true),
  );

  useEffect(() => {
    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    const onInstalled = () => {
      setDeferred(null);
      setInstalled(true);
    };
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!deferred) return { outcome: 'unavailable' };
    deferred.prompt();
    const choice = await deferred.userChoice;
    setDeferred(null);
    return choice; // { outcome: 'accepted' | 'dismissed', platform }
  }, [deferred]);

  return { canInstall: !!deferred, installed, promptInstall };
}

// ─── Online / offline hook ──────────────────────────────────
export function useOnlineStatus() {
  const [online, setOnline] = useState(
    typeof navigator === 'undefined' ? true : navigator.onLine,
  );
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);
  return online;
}

// ─── Offline POST queue ─────────────────────────────────────
function loadQueue() {
  try {
    return JSON.parse(localStorage.getItem(QUEUE_KEY) || '[]');
  } catch {
    return [];
  }
}

function saveQueue(items) {
  try {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(items));
  } catch {
    /* quota / disabled — accept the drop */
  }
}

/**
 * Enqueue a POST for retry when back online.
 * `payload` shape: { url, method, headers, body }
 * `body` must already be a JSON-serialisable object OR a string.
 */
export function enqueueOfflineRequest(payload) {
  const items = loadQueue();
  items.push({
    ...payload,
    enqueued_at: new Date().toISOString(),
    id: `q_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
  });
  saveQueue(items);
}

/** Try to flush every queued request. Silent per-item failures — they
 * stay in the queue and will be retried next time. */
export async function flushQueue() {
  const items = loadQueue();
  if (items.length === 0) return { flushed: 0, remaining: 0 };
  const remaining = [];
  let flushed = 0;
  for (const item of items) {
    try {
      const res = await fetch(item.url, {
        method: item.method || 'POST',
        headers: item.headers || { 'Content-Type': 'application/json' },
        body: typeof item.body === 'string' ? item.body : JSON.stringify(item.body),
        credentials: item.credentials || 'omit',
      });
      if (res.ok || (res.status >= 400 && res.status < 500)) {
        // 4xx = server rejected the payload as-is, no retry.
        flushed += 1;
      } else {
        remaining.push(item);
      }
    } catch {
      remaining.push(item);
    }
  }
  saveQueue(remaining);
  return { flushed, remaining: remaining.length };
}

// Auto-flush when connectivity returns.
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    flushQueue().catch(() => {});
  });
}

export function offlineQueueSize() {
  return loadQueue().length;
}
